import { useContext, useState, useEffect } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  Heading,
  Button,
  Datepicker,
  AgGrid,
  SelectDropdown,
  Spinner,
  ErrorMessage,
  CommonLink,
  InfoPopup,
  FileInput,
  MobileResponsiveGrid,
  SuccessPopup,
} from "@/common/components";
import {
  useGetProductInsuranceCompaniesQuery,
  useGetPurchaseTableMutation,
  useGetPurchaseTableHistoryMutation,
  useUpdatePurchaseTableMutation,
  useLazyGetInvestmentVehiclesForProductQuery,
} from "@/service/ProductService";
import dayjs from "dayjs";
import Papa from "papaparse";
import {
  formatDecimalField,
  formatNumberWithCommas,
  getFormattedDateTime,
  getProductOptionValue,
  validateAge,
  validatePrice,
} from "@/utils/Utilities";
import { Constants } from "@/utils/Constants";

const PurchaseTables = () => {
  const {
    labels,
    setNavHeader,
    setPageHeader,
    location,
    cancel,
    cloneDeep,
    uniqBy,
    accessorPrivileges,
    isRiccAdministrator,
  } = useContext(PrivateContext);

  const {
    data: insuranceCompanies,
    isFetching: isFetching1,
    error: error1,
  } = useGetProductInsuranceCompaniesQuery({
    productSponsorId: location?.state?.productSponsorId,
    productId: location?.state?.productId,
  });

  const [getPurchaseTableApi, { isLoading: isFetching2, error: error2 }] =
    useGetPurchaseTableMutation();
  const [investmentVehiclesApi, { isLoading: isFetching3, error: error3 }] =
    useLazyGetInvestmentVehiclesForProductQuery();
  const [
    getPurchaseTableHistoryApi,
    { isLoading: isFetching4, error: error4 },
  ] = useGetPurchaseTableHistoryMutation();
  const [updatePurchaseTableApi, { isLoading: isFetching5, error: error5 }] =
    useUpdatePurchaseTableMutation();

  const [investmentVehicleId, setInvestmentVehicleId] = useState("");
  const [investmentVehicleDropdownData, setInvestmentVehicleDropdownData] =
    useState();
  const [searchDate, setTableDate] = useState();
  const [purchaseTableRowData, setPurchaseTableRowData] = useState(null);
  const [purchaseTableHistoryRowData, setPurchaseTableHistoryRowData] =
    useState(null);
  const [showHistoryGrid, setShowHistoryGrid] = useState(false);
  const [maxResults, setMaxResults] = useState(100);
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [attachment, setAttachment] = useState("");
  const [csvErrors, setCsvErrors] = useState(null);
  const [parsedCsvData, setParsedCsvData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isPurchaseTableEdited, setIsPurchaseTableEdited] = useState(false);
  const [isPurchaseTableImported, setIsPurchaseTableImported] = useState(false);
  const [isCellEditing, setIsCellEditing] = useState(false);

  const purchaseTableColumnDefs = [
    { headerName: labels?.productDetail?.age, field: "ageYearCount", flex: 1 },
    {
      headerName: labels?.productDetail?.price,
      field: "singleSharePrice",
      editable: true,
      flex: 3,
      valueParser: (params) =>
        formatDecimalField(params, 0.0, 99999.9999999999, 10),
      cellRenderer: (params) => formatNumberWithCommas(params, 10),
      // (optional) keep editor simple text/number
      cellEditor: "agTextCellEditor",
    },
  ];

  const purchaseTableHistoryColumnDefs = [
    {
      headerName: labels?.productDetail?.date,
      field: "lastMntDateTime",
      cellRenderer: getFormattedDateTime,
      flex: 2,
    },
    {
      headerName: labels?.productDetail?.investmentVehicle,
      field: "investmentVehicleName",
      flex: 2,
    },
    {
      headerName: labels?.productDetail?.tableDate,
      field: "businessDayDate",
      flex: 2,
    },
    {
      headerName: labels?.productDetail?.age,
      field: "ageYearCount",
      sort: "asc",
      valueGetter: (params) => parseInt(params.data.ageYearCount, 10),
    },
    {
      headerName: labels?.productDetail?.price,
      field: "singleSharePrice",
      flex: 2,
      cellRenderer: (params) => formatNumberWithCommas(params, 2),
    },
    {
      headerName: labels?.productDetail?.operator,
      field: "lastMntOpId",
      flex: 2,
    },
  ];

  const fetchPurchaseTable = () => {
    const getPurchaseTablePayload = {
      productId: location?.state?.productId,
      productSponsorId: location?.state?.productSponsorId,
      insuranceCompanyId:
        insuranceCompanies?.productInsurers[0]?.insuranceCompanyId,
      investmentVehicleId: investmentVehicleId,
      insuranceCompanyEffectiveDate:
        insuranceCompanies?.productInsurers[0]?.effectiveDate,
      businessDayDate: searchDate,
    };

    getPurchaseTableApi(getPurchaseTablePayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) {
          setPurchaseTableRowData(cloneDeep(res?.insurerPurchaseTableAges));
        } else setPurchaseTableRowData([]);
      });
  };

  const fetchPurchaseTableHistory = () => {
    const fetchPurchaseTableHistoryPayload = {
      productId: location?.state?.productId,
      productSponsorId: location?.state?.productSponsorId,
      insuranceCompanyId:
        insuranceCompanies?.productInsurers[0]?.insuranceCompanyId,
      investmentVehicleId: investmentVehicleId,
      effectiveDate: insuranceCompanies?.productInsurers[0]?.effectiveDate,
      businessDayDate: searchDate,
      firstResult: 0,
      maxResults: maxResults,
    };

    getPurchaseTableHistoryApi(fetchPurchaseTableHistoryPayload)
      .unwrap()
      .then((res) => {
        if (!res?.errors?.length) {
          if (res?.moreRecords === "true") {
            setMaxResults(maxResults + 100);
          } else {
            setMaxResults(0);
          }
          setPurchaseTableHistoryRowData(res?.insurerPurchaseTableAgeAudits);
          setShowHistoryGrid(false);
        } else {
          setPurchaseTableHistoryRowData([]);
        }
      });
  };

  const updatePurchaseTable = () => {
    const baseFields = {
      productSponsorId: location?.state?.productSponsorId,
      productId: location?.state?.productId,
      insuranceCompanyId:
        insuranceCompanies?.productInsurers[0]?.insuranceCompanyId,
      investmentVehicleId: investmentVehicleId,
      insuranceCompanyEffectiveDate:
        insuranceCompanies?.productInsurers[0]?.effectiveDate,
      businessDayDate: searchDate,
    };

    const updatedRows = purchaseTableRowData.map((row) => ({
      ...baseFields,
      ageYearCount: row.ageYearCount,
      singleSharePrice: row.singleSharePrice,
      jointSharePrice: "0",
      lastMntDateTime: isPurchaseTableImported ? "" : row?.lastMntDateTime,
      lastMntOpId: isPurchaseTableImported ? "" : row?.lastMntOpId,
    }));

    const updatePurchaseTablePayload2 = {
      isImportFile: isPurchaseTableImported,
      ...baseFields,
      insurerPurchaseTableAges: updatedRows,
    };

    updatePurchaseTableApi(updatePurchaseTablePayload2)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
          setIsPurchaseTableEdited(false);
        }
      });
  };

  const importCsvDataFromPapa = (parsedRows, minAge, maxAge) => {
    const purchaseTables = [];

    parsedRows.forEach((row, index) => {
      if (!row || Object.values(row).every((val) => val.trim?.() === ""))
        return;

      const age = row[0]?.trim();
      const price = row[1]?.trim();

      validateAge(age, index, Constants.AGE_COLUMN);
      validatePrice(price, index, Constants.SINGLE_PRICE_COLUMN);

      const table = {
        ageYearCount: parseInt(age).toString(),
        singleSharePrice: parseFloat(price).toFixed(10).toString(),
      };

      purchaseTables.push(table);
    });

    if (!purchaseTables.length) {
      throw new Error("No data to import.");
    }

    purchaseTables.sort(
      (a, b) => parseInt(a.ageYearCount) - parseInt(b.ageYearCount)
    );

    const expectedCount = maxAge - minAge + 1;
    const actualCount = purchaseTables.length;

    if (expectedCount !== actualCount) {
      throw new Error(
        "Data range didn't match the defined min/max age values."
      );
    }

    const firstAge = parseInt(purchaseTables[0].ageYearCount);
    const lastAge = parseInt(
      purchaseTables[purchaseTables.length - 1].ageYearCount
    );

    if (firstAge !== minAge) {
      throw new Error("Minimum age didn't match the defined min age value.");
    }

    if (lastAge !== maxAge) {
      throw new Error("Maximum age didn't match the defined max age value.");
    }

    return purchaseTables;
  };

  const handleCsvFileChange = (files) => {
    setCsvErrors(null);
    setAttachment(files);
    setParsedCsvData(null);

    const file = files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvErrors({
        errorMessage: labels?.purchaseTable?.invalidFileError,
      });
      return;
    }

    const minAge = parseInt(
      getProductOptionValue(location, "PRODUCT_MIN_PURCHASE_TABLE_AGE_RANGE")
    );
    const maxAge = parseInt(
      getProductOptionValue(location, "PRODUCT_MAX_PURCHASE_TABLE_AGE_RANGE")
    );

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const parsed = importCsvDataFromPapa(
            result.data.slice(1),
            minAge,
            maxAge
          );
          setParsedCsvData(parsed);
        } catch (err) {
          setCsvErrors({
            errorMessage: `${labels?.purchaseTable?.csvImportError}, ${err["message"]}`,
          });
        }
      },
    });
  };

  const resetPurchaseTableStates = () => {
    setInvestmentVehicleId("");
    setTableDate();
    setPurchaseTableRowData(null);
    setPurchaseTableHistoryRowData(null);
    setShowHistoryGrid(false);
    setMaxResults(100);
    setShowImportPopup(false);
    setAttachment("");
    setCsvErrors(null);
    setParsedCsvData(null);
    setShowPopup(false);
    setIsPurchaseTableEdited(false);
    setIsPurchaseTableImported(false);
  };

  useEffect(() => {
    setPageHeader(
      labels?.productDetail?.purchaseTablePageFor + location?.state?.name
    );
    setNavHeader(labels?.productDetail?.purchaseTables);
  }, []);

  useEffect(() => {
    investmentVehiclesApi({
      productSponsorId: location?.state?.productSponsorId,
      productId: location?.state?.productId,
    })
      .unwrap()
      .then((res) => {
        const uniqueVehicles = uniqBy(
          res?.productInvestmentVehicles,
          "investmentVehicleId"
        );
        const dropdownData = uniqueVehicles.map((vehicle) => {
          return {
            investmentVehicleId: vehicle.investmentVehicleId,
            vehicleName: vehicle?.investmentVehicle?.name,
          };
        });
        setInvestmentVehicleDropdownData(dropdownData);
      });
  }, []);

  return (
    <>
      <section className="container-medium py-5">
        {(isFetching1 ||
          isFetching2 ||
          isFetching3 ||
          isFetching4 ||
          isFetching5) && <Spinner />}
        <ErrorMessage
          error={error1 || error3 || error2 || error4 || error5}
          wrapperClassName="mb-3"
        />

        <Heading
          text={labels?.productDetail?.searchForPurchaseTable}
          wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
          title={false}
        />

        <SelectDropdown
          id="investment-company-select"
          ariaLabel="Investment Company"
          dropdownClass="form-group"
          wrapperClassName="col-md-5"
          labelClass="mb-1"
          dropdownData={investmentVehicleDropdownData}
          label={labels?.productDetail?.investmentVehicle}
          value={investmentVehicleId}
          select={(value) => setInvestmentVehicleId(value)}
          selectClassName="col-12 px-3"
          defaultOption={true}
          optionId="investmentVehicleId"
          optionLabel="vehicleName"
        />

        <Datepicker
          id="table-date-purchase-table"
          label={labels?.productDetail?.tableDate}
          value={searchDate}
          wrapperClassName="form-group col-md-5"
          change={(value) => setTableDate(value)}
          max={dayjs()}
        />

        <div className="d-flex flex-md-row flex-row-reverse gap-1 mb-4">
          <Button
            key="searchBtnPurchaseTable"
            text={labels?.productDetail?.search}
            wrapperClassName="col-md-3 col-6"
            click={() => {
              fetchPurchaseTable();
              fetchPurchaseTableHistory();
            }}
            disabled={investmentVehicleId === "" || !searchDate}
          />
          <Button
            key="resetBtnPurchaseTable"
            type="secondary"
            text={labels?.productDetail?.reset}
            wrapperClassName="col-md-3 col-6"
            click={() => {
              resetPurchaseTableStates();
            }}
            disabled={investmentVehicleId === "" || !searchDate}
          />
        </div>

        <Heading
          text={labels?.productDetail?.purchaseTable}
          title={false}
          wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
        />

        {purchaseTableRowData === null ? (
          <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
            <div className="primary-color small">
              {labels?.productDetail?.selectInvestmentVehicleDate}
            </div>
          </div>
        ) : purchaseTableRowData.length === 0 ? (
          <>
            {accessorPrivileges["INSURANCE PURCHASE / PAYMENT TABLES"] !=
              "RETRIEVE" && (
              <CommonLink wrapperClassName="mb-2">
                <span onClick={() => setShowImportPopup(true)}>
                  {labels?.productDetail?.importPurchaseTable}
                </span>
              </CommonLink>
            )}

            <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
              <div className="primary-color small">
                {labels?.productDetail?.noExistingTable}
              </div>
            </div>

            {showImportPopup && (
              <div>
                <InfoPopup
                  heading={labels?.productDetail?.importPurchaseTable}
                  showModal={showImportPopup}
                  primaryButtonText="Import"
                  primaryButtonClick={() => {
                    if (!parsedCsvData) return;

                    setIsPurchaseTableImported(true);
                    setPurchaseTableRowData(parsedCsvData);
                    setIsPurchaseTableEdited(true);
                    setAttachment([]);
                    setParsedCsvData(null);
                    setCsvErrors(null);
                    setShowImportPopup(false);
                  }}
                  primaryButtonDisabled={
                    !attachment?.length || csvErrors?.errorMessage
                  }
                  secondaryButtonText="Cancel"
                  secondaryButtonClick={() => {
                    setAttachment([]);
                    setCsvErrors(null);
                    setShowImportPopup(false);
                  }}
                  body={
                    <>
                      <FileInput
                        value={attachment}
                        accept=".csv"
                        id="file-attachment"
                        onChange={(e) => {
                          handleCsvFileChange(e);
                        }}
                      />
                      <div className="text-start">
                        <ErrorMessage error={csvErrors} />
                      </div>
                    </>
                  }
                />
              </div>
            )}
          </>
        ) : (
          <>
            <AgGrid
              key="purchaseTableGrid"
              rowData={purchaseTableRowData}
              columnDefs={purchaseTableColumnDefs}
              domLayout="normal"
              wrapperClassNames="my-3 d-block"
              mainClassNames={"d-block"}
              // NEW: mobile-friendly edit behavior
              singleClickEdit
              stopEditingWhenCellsLoseFocus
              enterMovesDown
              enterMovesDownAfterEdit
              // Keep rows comfy for touch
              rowHeight={56}
              // Stable row identity (so updates don't jump)
              getRowId={({ data }) => String(data.ageYearCount)}
              onCellValueChanged={() => setIsPurchaseTableEdited(true)}
              onCellEditingStarted={() => setIsCellEditing(true)}
              onCellEditingStopped={() => setIsCellEditing(false)}
            />
            {(accessorPrivileges["INSURANCE PURCHASE / PAYMENT TABLES"] ===
              "ADD" ||
              isRiccAdministrator) && (
              <div className="d-flex justify-content-end my-4">
                <Button
                  key="applyBtnPurchaseTable"
                  text={labels?.productDetail?.apply}
                  wrapperClassName="col-md-2 col-12"
                  click={updatePurchaseTable}
                  disabled={!isPurchaseTableEdited || isCellEditing}
                />
              </div>
            )}
            <SuccessPopup
              showModal={showPopup}
              buttonText={labels?.productDetail?.continue}
              click={cancel}
              body={labels?.productDetail?.successMsg}
            />
          </>
        )}

        <Heading
          text={labels?.productDetail?.purchaseTableHistory}
          title={false}
          wrapperClassNames="h4 my-4 border-bottom border-black pb-2"
        />

        {purchaseTableHistoryRowData === null ? (
          <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
            <div className="primary-color small">
              {labels?.productDetail?.selectInvestmentVehicleDate}
            </div>
          </div>
        ) : purchaseTableHistoryRowData.length === 0 ? (
          <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
            <div className="primary-color small">
              {labels?.productDetail?.noHistory}
            </div>
          </div>
        ) : (
          <CommonLink wrapperClassName="grid-detail-box d-flex justify-content-center my-2">
            <span onClick={() => setShowHistoryGrid((prev) => !prev)}>
              {showHistoryGrid ? "Hide" : "Show"} purchase table history
            </span>
          </CommonLink>
        )}
      </section>

      <>
        {showHistoryGrid && (
          <>
            <AgGrid
              rowData={purchaseTableHistoryRowData}
              columnDefs={purchaseTableHistoryColumnDefs}
              domLayout="normal"
              showPrintTable={false}
              wrapperClassNames="container-large pb-5 d-md-block d-none"
            >
              {maxResults ? (
                <CommonLink wrapperClassName="d-flex justify-content-end mb-2">
                  <span onClick={() => fetchPurchaseTableHistory()}>
                    {labels?.productDetail?.more}
                  </span>
                </CommonLink>
              ) : (
                ""
              )}
            </AgGrid>
            <div className="pb-5">
              <MobileResponsiveGrid
                key="purchaseTableHistoryMobileGrid"
                columnDefs={purchaseTableHistoryColumnDefs}
                rowData={purchaseTableHistoryRowData}
              >
                {maxResults ? (
                  <CommonLink wrapperClassName="d-flex justify-content-end mb-2">
                    <span onClick={() => fetchPurchaseTableHistory()}>
                      {labels?.productDetail?.more}
                    </span>
                  </CommonLink>
                ) : (
                  ""
                )}
              </MobileResponsiveGrid>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default PurchaseTables;
